import { CfoFinancialData } from "@services/cfo/cfo.api";
import { WIDGET_METRICS_CONFIG } from "@services/math/math.const";

export interface TableRowData {
  key: string;
  pnlType: string;
  asIs: string;
  children?: TableRowData[];
  metricType?: string;
}

const formatValue = (value: number, metricType: string): string => {
  if (
    metricType.includes("margin") ||
    metricType === "roi" ||
    metricType === "acos" ||
    metricType === "tacos" ||
    metricType === "gross_margin" ||
    metricType === "contribution_margin" ||
    metricType === "net_margin"
  ) {
    return `${value.toFixed(1)}%`;
  }

  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const METRICS_ORDER = [
  "total_sales",
  "cost_of_goods_sold",
  "overhead_cost",
  "gross_profit",
  "gross_margin",
  "contribution_profit",
  "contribution_margin",
  "opex",
  "net_profit",
  "net_margin",
  "roi",
  "acos",
  "tacos",
];

const METRIC_LABELS = {
  total_sales: "Total Sales",
  amazon_sales: "Amazon Sales",
  amazon_ad_sales: "Amazon Ad Sales",
  sponsored_brands: "Sponsored Brands",
  sponsored_display: "Sponsored Display",
  sponsored_products: "Sponsored Products",
  amazon_other_sales: "Amazon Other Sales",
  organic_social: "Organic Social",
  other_sales: "Other Sales",
  dsp_sales: "DSP Sales",
  tiktok_sales: "TikTok Sales",
  tiktok_ad_sales: "TikTok Ad Sales",
  tiktok_other_sales: "TikTok Other Sales",
  shopify_sales: "Shopify Sales",
  shopify_ad_sales: "Shopify Ad Sales",
  shopify_other_sales: "Shopify Other Sales",
  cost_of_goods_sold: "Cost of Goods Sold",
  amazon_cogs: "Amazon COGS",
  tiktok_cogs: "TikTok COGS",
  shopify_cogs: "Shopify COGS",
  cogs: "COGS",
  shipping_handling: "Shipping & Handling",
  overhead_cost: "Overhead Cost",
  amazon_cost: "Amazon Cost",
  amazon_fees: "Amazon Fees",
  ad_spend: "Ad Spend",
  other_fees: "Other Fees",
  tiktok_cost: "TikTok Cost",
  tiktok_fees: "TikTok Fees",
  tiktok_ad_spend: "TikTok Ad Spend",
  shopify_cost: "Shopify Cost",
  shopify_fees: "Shopify Fees",
  shopify_ad_spend: "Shopify Ad Spend",
  gross_profit: "Gross Profit",
  amazon: "Amazon",
  tiktok: "TikTok",
  shopify: "Shopify",
  gross_margin: "Gross Margin",
  contribution_profit: "Contribution Profit",
  contribution_margin: "Contribution Margin",
  opex: "OPEX",
  net_profit: "Net Profit",
  net_margin: "Net Margin",
  roi: "ROI",
  acos: "ACOS",
  tacos: "TACOS",
};

export const transformFinancialData = (
  data: CfoFinancialData,
): TableRowData[] => {
  const createChildren = (
    components: any,
    parentKey: string,
    parentType: string,
  ): TableRowData[] => {
    if (!components || typeof components !== "object") {
      return [];
    }

    const isParentPercentage =
      parentType.includes("margin") ||
      parentType === "roi" ||
      parentType === "acos" ||
      parentType === "tacos";

    return Object.entries(components).map(([key, value], index) => {
      const childKey = `${parentKey}-${key}-${index}`;
      const label =
        METRIC_LABELS[key as keyof typeof METRIC_LABELS] ||
        key
          .replace(/_/g, " ")
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");

      const rawValue =
        typeof value === "number" ? value : ((value as any)?.value ?? 0);

      const formatType = isParentPercentage ? parentType : key;

      const childRow: TableRowData = {
        key: childKey,
        pnlType: label,
        asIs: formatValue(rawValue, formatType),
        metricType: key,
      };

      if (
        typeof value === "object" &&
        value !== null &&
        (value as any).components
      ) {
        childRow.children = createChildren(
          (value as any).components,
          childKey,
          key,
        );
      }

      return childRow;
    });
  };

  const sortedEntries = Object.entries(data).sort(([keyA], [keyB]) => {
    const indexA = METRICS_ORDER.indexOf(keyA);
    const indexB = METRICS_ORDER.indexOf(keyB);

    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;

    return indexA - indexB;
  });

  return sortedEntries.map(([key, item], index) => {
    const label =
      (METRIC_LABELS[key as keyof typeof METRIC_LABELS] ||
        WIDGET_METRICS_CONFIG?.[key]?.label) ??
      key.replace(/_/g, " ");

    const rowData: TableRowData = {
      key: `row-${index}`,
      pnlType: label,
      asIs: formatValue(item.value, key),
      metricType: key,
    };

    if (item.components && Object.keys(item.components).length > 0) {
      rowData.children = createChildren(item.components, `row-${index}`, key);
    }

    return rowData;
  });
};
